import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Calendar as CalendarIcon, Clock, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useT } from '../i18n.jsx';

const CalendarUser = () => {
  const { t, locale } = useT();
  // Stored day and period types are compared against their English words everywhere else in the
  // platform, so they stay as they are and only the shown word goes through the dictionary. The
  // stored value is the fallback: a school that invents a type still reads its own word.
  const storedWord = (group, value) => t(
    `myCalendar.${group}.${String(value || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, value,
  );
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [timetableProfiles, setTimetableProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const { activeSchoolId, isMobile } = useOutletContext();

  useEffect(() => {
    if (activeSchoolId) fetchData();
  }, [activeSchoolId]);

  const fetchData = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      // Fetch timetable profiles
      const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
      const pList = [];
      pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
      setTimetableProfiles(pList);

      // Fetch active year events (simplified to fetch all events for now, ideally filter by date range)
      // For performance in real app, we'd query by date range.
      const eSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events')));
      const eList = [];
      eSnap.forEach(d => eList.push({ uid: d.id, ...d.data() }));
      setCalendarEvents(eList);
      
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getDayEvent = (dateStr) => {
    return calendarEvents.find(e => e.date === dateStr);
  };

  const getTimetable = (eventId) => {
    const ev = getDayEvent(selectedDate);
    if (!ev) return null;

    let profile;
    if (!ev.timetableProfileId || ev.timetableProfileId === 'default') {
      profile = timetableProfiles.find(p => p.isDefault);
    } else {
      profile = timetableProfiles.find(p => p.uid === ev.timetableProfileId);
    }
    return profile;
  };

  const currentEvent = getDayEvent(selectedDate);
  const currentTimetable = getTimetable();

  // Helper to get next 7 days for the mini-nav
  const getNextDays = () => {
    const days = [];
    const curr = new Date(selectedDate);
    // Go back 2 days
    curr.setDate(curr.getDate() - 2);
    for (let i = 0; i < 7; i++) {
      days.push(curr.toISOString().split('T')[0]);
      curr.setDate(curr.getDate() + 1);
    }
    return days;
  };

  const isToday = (dateStr) => dateStr === new Date().toISOString().split('T')[0];

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CalendarIcon size={24} /> {t('myCalendar.title')}
        </h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input 
            type="date" 
            className="input-field" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)} 
          />
          <button onClick={fetchData} className="btn-outline" title={t('common.refresh')} aria-label={t('common.refresh')} style={{ padding: '0.5rem' }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {getNextDays().map(d => {
          const ev = getDayEvent(d);
          const isSelected = d === selectedDate;
          const dayDate = new Date(d);
          return (
            <button
              key={d}
              onClick={() => setSelectedDate(d)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '0.75rem 1rem', minWidth: '80px', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                backgroundColor: isSelected ? 'var(--color-primary)' : 'var(--color-bg-card)',
                color: isSelected ? 'white' : 'var(--color-text-main)',
                boxShadow: isSelected ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                border: isToday(d) && !isSelected ? '2px solid var(--color-primary)' : 'none',
                opacity: (ev && ev.type === 'School Day') ? 1 : 0.6
              }}
            >
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '700', marginBottom: '0.25rem' }}>
                {dayDate.toLocaleDateString(locale, { weekday: 'short' })}
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800' }}>
                {dayDate.getDate()}
              </div>
              <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', fontWeight: '600', // A background token was being used as a text colour here, which happened to look like
                // pale-on-dark and broke the moment the palette moved. The selected cell is filled
                // with the primary, so the accent is what reads on it.
                color: isSelected ? 'var(--color-accent)' : 'var(--color-primary)' }}>
                {ev?.cycleDay || '---'}
              </div>
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flex: 1, minHeight: 0 }}>
        
        {/* Day Info Card */}
        <div style={{ width: '300px', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', padding: isMobile ? '1rem' : '2rem', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '1rem', color: 'var(--color-text-muted)', fontWeight: '600' }}>
              {new Date(selectedDate).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            
            {loading ? (
              <div className="mt-4"><RefreshCw className="animate-spin text-muted mx-auto" size={24} /></div>
            ) : !currentEvent ? (
              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--color-warning-bg)', borderRadius: '8px', color: 'var(--color-warning-text)', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <AlertCircle size={20} /> {t('myCalendar.notInAcademicYear')}
              </div>
            ) : (
              <>
                <div style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--color-primary)', marginTop: '1rem', lineHeight: '1' }}>
                  {currentEvent.cycleDay || storedWord('dayTypes', currentEvent.type)}
                </div>
                
                <div style={{ marginTop: '0.5rem', padding: '0.25rem 0.75rem', backgroundColor: currentEvent.type === 'School Day' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)', color: currentEvent.type === 'School Day' ? 'var(--color-success-text)' : 'var(--color-danger-text)', borderRadius: '999px', display: 'inline-block', fontSize: '0.875rem', fontWeight: '600' }}>
                  {storedWord('dayTypes', currentEvent.type)}
                </div>

                {currentEvent.description && (
                  <div style={{ marginTop: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--color-text-main)' }}>
                    {currentEvent.description}
                  </div>
                )}
              </>
            )}
          </div>
          
          <div style={{ marginTop: 'auto', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t('myCalendar.timetableProfile')}</div>
            <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} className="text-primary" /> 
              {currentTimetable ? currentTimetable.name : t('myCalendar.noTimetable')}
            </div>
          </div>
        </div>

        {/* Timetable View */}
        <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', padding: isMobile ? '1rem' : '2rem', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', overflowY: 'auto' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--color-text-main)' }}>{t('myCalendar.dailySchedule')}</h3>
          
          {!currentTimetable ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--color-text-muted)', gap: '1rem' }}>
              <CalendarIcon size={48} opacity={0.2} />
              <span>{t('myCalendar.noPeriods')}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {currentTimetable.periods.map((p, idx) => {
                let bg = 'white';
                let iconColor = 'var(--color-primary)';
                if (p.type === 'Break') { bg = 'var(--color-warning-bg)'; iconColor = 'var(--color-warning-text)'; }
                if (p.type === 'Admin') { bg = 'var(--color-bg-subtle)'; iconColor = 'var(--color-text-subtle)'; }

                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: bg, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    <div style={{ width: '120px', fontWeight: '700', color: 'var(--color-text-main)', fontSize: '1.1rem' }}>
                      {p.startTime}
                    </div>
                    <ArrowRight size={16} color="var(--color-text-muted)" style={{ margin: '0 1rem' }} />
                    <div style={{ width: '120px', fontWeight: '700', color: 'var(--color-text-muted)' }}>
                      {p.endTime}
                    </div>
                    
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '2rem', borderLeft: '2px solid var(--color-border)' }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-text-main)' }}>{p.name}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: iconColor, backgroundColor: bg === 'white' ? 'var(--color-info-bg)' : 'transparent', padding: bg === 'white' ? '0.2rem 0.5rem' : 0, borderRadius: '4px' }}>
                        {storedWord('periodTypes', p.type)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

        </div>
      </div>
      
    </div>
  );
};

export default CalendarUser;
